<template>
  <div
    class="obituary-timeline__year-group"
    :class="{
      'obituary-timeline__year-group--fade-left': !prevDisabled,
      'obituary-timeline__year-group--fade-right': !nextDisabled,
    }"
  >
    <header class="obituary-timeline__year-header">
      <h3
        class="obituary-timeline__year-title"
        :class="{ 'obituary-timeline__year-title--play': playable }"
      >
        <LifeStoryPill
          v-if="playable"
          :label="playLabel"
          :text="displayYear"
          @click="$emit('play')"
        />
        <template v-else>{{ displayYear }}</template>
      </h3>
    </header>

    <slider
      classes="obituary-timeline__slider"
      @update:next-disabled="setNextDisabled"
      @update:prev-disabled="setPrevDisabled"
    >
      <div
        class="obituary-timeline__card"
        v-for="(image, index) in images"
        :key="index"
      >
        <button
          class="obituary-timeline__card-image-wrapper"
          type="button"
          :aria-label="openImageLabel"
          @click="() => $emit('image-click', index)"
        >
          <ix-picture>
            <ix-source
              media="(max-width:479px)"
              :imgix-params="{ w: 280, ...imgixParams }"
              :src="image.src"
            />
            <ix-source
              media="(min-width:480px)"
              :imgix-params="{ w: 320, ...imgixParams }"
              :src="image.src"
            />
            <ix-img
              class="obituary-timeline__card-image"
              height="240"
              loading="lazy"
              width="320"
              :alt="image.text || ''"
              :imgix-params="{ w: 320, ...imgixParams }"
              :src="image.src"
            />
          </ix-picture>
        </button>
        <div class="obituary-timeline__card-footer">
          <div class="obituary-timeline__card-footer-meta">
            <p
              class="obituary-timeline__card-caption"
              v-if="image.text"
              :class="{ 'is-open': expanded[index] }"
              :ref="(el) => setCaption(index, el)"
              v-text="image.text"
            />
            <button
              class="obituary-timeline__more"
              type="button"
              v-if="image.text && (overflowing[index] || expanded[index])"
              :aria-expanded="!!expanded[index]"
              @click="toggle(index)"
              v-text="expanded[index] ? texts.showLess : texts.showMore"
            />
            <span
              class="obituary-timeline__card-year"
              v-if="image.yearTaken"
              v-text="image.yearTaken"
            />
          </div>
          <more-comment-actions
            v-if="image.commentId"
            :comment="{ id: image.commentId }"
          />
        </div>
      </div>
    </slider>
  </div>
</template>

<script>
  import Slider from "Components/Slider.vue";
  import { sprintf } from "sprintf-js";
  import { formatDecade } from "../timeline-helpers";
  import MoreCommentActions from "./MoreCommentActions.vue";
  import LifeStoryPill from "./Slideshow/LifeStoryPill.vue";

  export default {
    beforeUnmount() {
      window.removeEventListener("resize", this.measure);
    },
    components: {
      LifeStoryPill,
      MoreCommentActions,
      Slider,
    },

    computed: {
      displayYear() {
        return formatDecade(this.year);
      },
      openImageLabel() {
        return lavendlaObituary?.common?.openImage || "";
      },
      playLabel() {
        const text = lavendlaObituary?.slideshow?.playDecade || "%s";
        return sprintf(text, this.displayYear);
      },
    },

    data() {
      return {
        captions: [],
        expanded: {},
        imgixParams: {
          ar: "4:3",
          crop: "faces,edges",
          fit: "crop",
        },
        nextDisabled: false,
        overflowing: {},
        prevDisabled: true,
        texts: lavendlaObituary?.common || {},
      };
    },

    emits: ["image-click", "play"],

    methods: {
      /** Which captions run past their two lines. */
      measure() {
        const overflowing = {};
        this.captions.forEach((el, i) => {
          if (el && !this.expanded[i]) {
            overflowing[i] = el.scrollHeight > el.clientHeight + 1;
          }
        });
        this.overflowing = overflowing;
      },

      setCaption(index, el) {
        this.captions[index] = el;
      },

      setNextDisabled(value) {
        this.nextDisabled = value;
      },

      setPrevDisabled(value) {
        this.prevDisabled = value;
      },

      toggle(index) {
        this.expanded = { ...this.expanded, [index]: !this.expanded[index] };
      },
    },

    mounted() {
      this.$nextTick(this.measure);
      // Line breaks depend on the real fonts: measure again once they are in.
      document.fonts?.ready.then(() => this.measure());
      window.addEventListener("resize", this.measure);
    },

    name: "TimelineYearGroup",

    props: {
      images: {
        required: true,
        type: Array,
      },
      playable: {
        default: false,
        type: Boolean,
      },
      year: {
        required: true,
        type: String,
      },
    },

    watch: {
      images() {
        this.expanded = {};
        this.$nextTick(this.measure);
      },
    },
  };
</script>
